import { useMemo } from 'react'
import { resourceManager } from '@/Core/Managers/Resource'
import { GetRestUrlTask } from '@/Core/Tasks/Resources/GetRestUrlTask'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'

/**
 * Дані ресурсу довідника для навігації та огляду
 */
export interface ListResourceMenuItem {
    /** Ключ ресурсу, напр. `Setting.ListCargo` */
    key: string
    /** Заголовок для відображення */
    title: string
    /** Останній сегмент pageUrl, напр. `cargos` */
    path: string
    /** Повний шлях сторінки, напр. `/settings/cargos` */
    pageUrl: string
    /** URL колекції в API, напр. `/services/lists/cargos` */
    restUrl: string
}

/**
 * Хук для отримання динамічного списку ресурсів Setting.List*
 *
 * @param prefix - префікс для фільтрації ресурсів (за замовчуванням 'Setting.List')
 * @returns масив ресурсів, відсортований за назвою
 *
 * @example
 * ```tsx
 * const listResources = useListResources()
 * // [{ key: 'Setting.ListCargo', title: 'Вантажі', path: 'cargos',
 * //    pageUrl: '/settings/cargos', restUrl: '/services/lists/cargos' }, …]
 * ```
 */
export const useListResources = (prefix: string = 'Setting.List'): ListResourceMenuItem[] => {
    const listResources = useMemo(() => {
        const allResources = resourceManager.getAll()
        const result: ListResourceMenuItem[] = []

        // Фільтруємо ресурси за префіксом
        Object.entries(allResources).forEach(([key, resource]: [string, IResourceConfig]) => {
            if (!key.startsWith(prefix)) {
                return
            }

            // Перевіряємо наявність необхідних полів; restUrl може бути рядком або IRestUrlConfig
            const restUrl = GetRestUrlTask(resource.restUrl, 'getCollection')
            if (!resource.title || !resource.pageUrl || !restUrl) {
                console.warn(`[useListResources] Ресурс ${key} не має title, pageUrl або restUrl`, resource)
                return
            }

            // Витягуємо шлях з pageUrl
            // pageUrl має формат '/settings/currencies', нам потрібен 'currencies'
            const pathParts = resource.pageUrl.split('/')
            const path = pathParts[pathParts.length - 1]

            if (!path) {
                console.warn(`[useListResources] Не вдалося витягти path з pageUrl для ресурсу ${key}`, resource)
                return
            }

            result.push({
                key,
                title: resource.title,
                path,
                pageUrl: resource.pageUrl,
                restUrl,
            })
        })

        // Сортуємо за назвою для послідовного відображення
        result.sort((a, b) => a.title.localeCompare(b.title, 'uk'))

        return result
    }, [prefix])

    return listResources
}
